import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import type { Department } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { roundToNearestHour } from "../../utils/dateUtils";
import type { BaseNurseShift, NurseFullRole, OpenShiftRequest, ReplaceShiftRequest, ShiftStaffTimes, StaffShiftMap } from "../../utils/models";
import { getJobDisplayName } from "../../utils/roles";
import { inverseRolesMap } from "../../utils/utilFunctions";

type GroupingMode = "role" | "roleGroup" | "job";

interface UseTimelineProps {
	requests: (OpenShiftRequest | ReplaceShiftRequest)[];
	nurses: BaseNurseShift[];
	useRoleGroups?: boolean;
	groupingMode?: GroupingMode;
}

const createStableHashes = (nurses: BaseNurseShift[], requests: (OpenShiftRequest | ReplaceShiftRequest)[]) => {
	// Include jobId/jobName so timeline rows (job-colored bars, labels) refresh when job is edited without full reload
	const nursesHash = nurses.map((n) => `${n.role}-${n.startTime.toSeconds()}-${n.endTime.toSeconds()}-${n.name}-${n.jobId ?? ""}-${n.jobName ?? ""}`).join("|");

	const requestsHash = requests
		.map(
			(r) =>
				`${r.message.roles.join(",")}-${r.message.shift_start.toSeconds()}-${r.message.shift_end.toSeconds()}-${(r as OpenShiftRequest).needed_nurses}-${r.job_id ?? ""}`,
		)
		.join("|");

	return { nursesHash, requestsHash };
};

const findRoleGroup = (role: string, roleGroups: Record<string, string[]>): string | null => {
	for (const [groupName, roles] of Object.entries(roleGroups)) {
		if (roles.includes(inverseRolesMap[role as NurseFullRole])) {
			return groupName;
		}
	}
	return null;
};

const findOrCreateShift = (staffShiftMap: StaffShiftMap, key: string, startTime: DateTime, endTime: DateTime): number => {
	if (!staffShiftMap[key]) {
		staffShiftMap[key] = [];
		return -1;
	}
	const startUnix = startTime.toSeconds();
	const endUnix = endTime.toSeconds();

	for (let i = 0; i < staffShiftMap[key].length; i++) {
		const shift = staffShiftMap[key][i];
		if (shift.startTime.toSeconds() === startUnix && shift.endTime.toSeconds() === endUnix) {
			return i;
		}
	}
	return -1;
};

const processNurses = (
	nurses: BaseNurseShift[],
	staffShiftMap: StaffShiftMap,
	roleGroups: Record<string, string[]>,
	useRoleGroups: boolean,
	timeTransform?: (time: DateTime, cache: Map<number, DateTime>) => DateTime,
	cache?: Map<number, DateTime>,
	groupingMode?: GroupingMode,
) => {
	for (const nurse of nurses) {
		if (!nurse.startTime.diff(nurse.endTime, "minutes").as("minutes")) continue;

		const startTime = timeTransform && cache ? timeTransform(nurse.startTime, cache) : roundToNearestHour(nurse.startTime);
		const endTime = timeTransform && cache ? timeTransform(nurse.endTime, cache) : roundToNearestHour(nurse.endTime);

		let key: string;
		let roleGroup;

		roleGroup = findRoleGroup(nurse.role, roleGroups);

		if (groupingMode === "job" && nurse.jobName) {
			key = `${nurse.jobName}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
		} else if (useRoleGroups && roleGroup) {
			key = `${roleGroup}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
		} else {
			key = `${nurse.role}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
		}

		staffShiftMap[key] ||= [];
		const index = findOrCreateShift(staffShiftMap, key, startTime, endTime);

		if (index >= 0) {
			const shift = staffShiftMap[key][index];
			shift.count += 1;
			shift.names?.push(nurse.name);
		} else {
			const shiftData: ShiftStaffTimes = {
				count: 1,
				startTime,
				endTime,
				role: nurse.role,
				actualStartTime: nurse.startTime,
				actualEndTime: nurse.endTime,
				names: [nurse.name],
			};

			if (groupingMode === "job" && nurse.jobName) {
				shiftData.jobName = nurse.jobName;
				shiftData.jobId = nurse.jobId;
			} else if (useRoleGroups && roleGroup) {
				shiftData.roleGroup = roleGroup;
			}

			staffShiftMap[key].push(shiftData);
		}
	}
};

const processRequests = (
	requests: (OpenShiftRequest | ReplaceShiftRequest)[],
	staffShiftMap: StaffShiftMap,
	roleGroups: Record<string, string[]>,
	useRoleGroups: boolean,
	timeTransform?: (time: DateTime, cache: Map<number, DateTime>) => DateTime,
	cache?: Map<number, DateTime>,
	groupingMode?: GroupingMode,
	chosenDepartment?: Department,
) => {
	for (const request of requests) {
		const startTime = timeTransform && cache ? timeTransform(request.message.shift_start, cache) : roundToNearestHour(request.message.shift_start);
		const endTime = timeTransform && cache ? timeTransform(request.message.shift_end, cache) : roundToNearestHour(request.message.shift_end);
		const neededNurses = (request as OpenShiftRequest).needed_nurses;

		const jobName = groupingMode === "job" && chosenDepartment ? getJobDisplayName(request.job_id, chosenDepartment) : "";
		if (jobName) {
			const key = `${jobName}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
			staffShiftMap[key] ||= [];
			const index = findOrCreateShift(staffShiftMap, key, startTime, endTime);

			if (index >= 0) {
				const shift = staffShiftMap[key][index];
				shift.requestedCount = (shift.requestedCount || 0) + neededNurses;
			} else {
				staffShiftMap[key].push({
					count: 0,
					requestedCount: neededNurses,
					startTime,
					endTime,
					role: request.message.roles[0] as NurseFullRole,
					jobName,
					jobId: request.job_id,
					actualStartTime: request.message.shift_start,
					actualEndTime: request.message.shift_end,
				});
			}
			continue;
		}

		if (useRoleGroups) {
			const roleGroupCounts: Record<string, number> = {};

			const roleGroup = findRoleGroup(request.message.roles[0], roleGroups);

			if (roleGroup) {
				roleGroupCounts[roleGroup] = (roleGroupCounts[roleGroup] || 0) + 1;
			}

			for (const [roleGroup, roleCount] of Object.entries(roleGroupCounts)) {
				const key = `${roleGroup}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
				staffShiftMap[key] ||= [];
				const index = findOrCreateShift(staffShiftMap, key, startTime, endTime);

				const requestedCount = neededNurses * roleCount;

				if (index >= 0) {
					const shift = staffShiftMap[key][index];
					shift.requestedCount = (shift.requestedCount || 0) + requestedCount;
				} else {
					const representativeRole = roleGroups[roleGroup][0] as NurseFullRole;

					staffShiftMap[key].push({
						count: 0,
						requestedCount: requestedCount,
						startTime,
						endTime,
						role: representativeRole,
						roleGroup,
						actualStartTime: request.message.shift_start,
						actualEndTime: request.message.shift_end,
					});
				}
			}
		} else {
			for (const role of request.message.roles) {
				const key = `${role}/${startTime.toSeconds()}/${endTime.toSeconds()}`;
				staffShiftMap[key] ||= [];
				const index = findOrCreateShift(staffShiftMap, key, startTime, endTime);

				if (index >= 0) {
					const shift = staffShiftMap[key][index];
					shift.requestedCount = (shift.requestedCount || 0) + neededNurses;
				} else {
					staffShiftMap[key].push({
						count: 0,
						requestedCount: neededNurses,
						startTime,
						endTime,
						role: role as NurseFullRole,
						actualStartTime: request.message.shift_start,
						actualEndTime: request.message.shift_end,
					});
				}
			}
		}
	}
};

const createTimelineRows = (
	staffShiftMap: StaffShiftMap,
	doShiftsOverlap: (shift1: ShiftStaffTimes, shift2: ShiftStaffTimes) => boolean,
	useRoleGroups: boolean,
	sortByGroup = true,
) => {
	const rowGroups: {
		[groupKey: string]: {
			[row: number]: { key: string; shift: ShiftStaffTimes }[];
		};
	} = {};

	for (const [key, shifts] of Object.entries(staffShiftMap)) {
		const groupKey = key.split("/")[0];

		const keyShifts = shifts.map((shift) => ({
			key,
			shift,
			totalCount: shift.count + (shift.requestedCount || 0),
		}));

		keyShifts.sort((a, b) => {
			if (sortByGroup) {
				const groupA = a.key.split("/")[0];
				const groupB = b.key.split("/")[0];
				if (groupA !== groupB) {
					return groupA.localeCompare(groupB);
				}
			}
			return b.totalCount - a.totalCount;
		});

		if (!rowGroups[groupKey]) {
			rowGroups[groupKey] = {};
		}

		for (const shiftItem of keyShifts) {
			let rowIndex = 0;
			let placed = false;

			while (!placed) {
				const currentRow = (rowGroups[groupKey][rowIndex] ??= []);

				let canPlace = true;
				for (const existingItem of currentRow) {
					if (doShiftsOverlap(shiftItem.shift, existingItem.shift)) {
						canPlace = false;
						break;
					}
				}

				if (canPlace) {
					currentRow.push(shiftItem);
					placed = true;
				} else {
					rowIndex++;
				}
			}
		}
	}

	const sortedRowGroups: typeof rowGroups = {};
	const groupNames = Object.keys(rowGroups).sort();

	for (const groupName of groupNames) {
		sortedRowGroups[groupName] = rowGroups[groupName];
	}

	return sortedRowGroups;
};

export const useTimeline = ({ nurses, requests, useRoleGroups = false, groupingMode }: UseTimelineProps) => {
	const { chosenDepartment } = useHospitalData();

	const doShiftsOverlap = useCallback((shift1: ShiftStaffTimes, shift2: ShiftStaffTimes): boolean => {
		return shift1.startTime.toSeconds() < shift2.endTime.toSeconds() && shift2.startTime.toSeconds() < shift1.endTime.toSeconds();
	}, []);

	const { nursesHash, requestsHash } = useMemo(() => createStableHashes(nurses, requests), [nurses, requests]);

	const staffShiftMap: StaffShiftMap = useMemo(() => {
		const staffShiftMap: StaffShiftMap = {};

		processNurses(nurses, staffShiftMap, chosenDepartment.role_groups, useRoleGroups, undefined, undefined, groupingMode);
		processRequests(requests, staffShiftMap, chosenDepartment.role_groups, useRoleGroups, undefined, undefined, groupingMode, chosenDepartment);

		const sortedKeys: string[] = Object.keys(staffShiftMap).sort((keyA, keyB) => {
			const aTotalCount = staffShiftMap[keyA].reduce((sum, shift) => {
				return sum + shift.count + (shift.requestedCount || 0);
			}, 0);

			const bTotalCount = staffShiftMap[keyB].reduce((sum, shift) => {
				return sum + shift.count + (shift.requestedCount || 0);
			}, 0);

			return bTotalCount - aTotalCount;
		});

		const sortedStaffShiftMap: StaffShiftMap = {};
		for (const key of sortedKeys) {
			sortedStaffShiftMap[key] = staffShiftMap[key];
		}

		return sortedStaffShiftMap;
	}, [nursesHash, requestsHash, chosenDepartment, useRoleGroups, groupingMode]);

	const timelineRows = useMemo(() => createTimelineRows(staffShiftMap, doShiftsOverlap, useRoleGroups, true), [staffShiftMap, doShiftsOverlap, useRoleGroups]);

	return { timelineRows };
};

export const useHighLevelTimeline = ({ nurses, requests, useRoleGroups = false }: UseTimelineProps) => {
	const { chosenDepartment } = useHospitalData();
	const roundToFourHourBlock = useCallback((time: DateTime, cache: Map<number, DateTime>): DateTime => {
		const unix = time.toSeconds();

		if (cache.has(unix)) {
			return cache.get(unix)!;
		}

		const hour = time.hour;
		const roundedHour = Math.floor(hour / 4) * 4;
		const rounded = time.set({ hour: roundedHour }).startOf("hour");

		cache.set(unix, rounded);
		return rounded;
	}, []);

	const doShiftsOverlap = useCallback((shift1: ShiftStaffTimes, shift2: ShiftStaffTimes): boolean => {
		return shift1.startTime.toSeconds() < shift2.endTime.toSeconds() && shift2.startTime.toSeconds() < shift1.endTime.toSeconds();
	}, []);

	const { nursesHash, requestsHash } = useMemo(() => createStableHashes(nurses, requests), [nurses, requests]);

	const highLevelStaffShiftMap: StaffShiftMap = useMemo(() => {
		const staffShiftMap: StaffShiftMap = {};
		const timeRoundingCache = new Map<number, DateTime>();

		processNurses(nurses, staffShiftMap, chosenDepartment.role_groups, useRoleGroups, roundToFourHourBlock, timeRoundingCache);
		processRequests(requests, staffShiftMap, chosenDepartment.role_groups, useRoleGroups, roundToFourHourBlock, timeRoundingCache);

		return staffShiftMap;
	}, [nursesHash, requestsHash, roundToFourHourBlock, chosenDepartment.role_groups, useRoleGroups]);

	const highLevelTimelineRows = useMemo(
		() => createTimelineRows(highLevelStaffShiftMap, doShiftsOverlap, useRoleGroups, false),
		[highLevelStaffShiftMap, doShiftsOverlap, useRoleGroups],
	);

	return { timelineRows: highLevelTimelineRows };
};
